/**
 * Browser-proof stamp — writes a cart attribute proving this cart was built
 * by a real browser executing theme JavaScript via normal storefront paths.
 * The order-analysis backend reads it from the order's note_attributes;
 * carts created by bots that skip the storefront (cart permalinks, direct
 * checkout, scripted carts) never receive it.
 *
 * Token: v1.<unix_seconds>.<last 12 chars of cart token>.<flags>
 *   - cart-token suffix binds the stamp to this cart (replay tell)
 *   - flags: 'h' human interaction seen / 'n' none, then automation tells:
 *     'w' = navigator.webdriver announced (stock Puppeteer/Playwright/Selenium),
 *     'x' = cheap headless tells (HeadlessChrome UA, zero outer window,
 *           Chrome UA without window.chrome). No fingerprinting — these are
 *     flags automation frameworks announce, not collected entropy.
 *
 * Every path is fail-silent: the storefront must never break because of this.
 */
(function () {
  'use strict';

  var ATTR = '__erleia_bp';
  var IKEY = '__erleia_bp_i';
  var RESTAMP_MS = 30 * 60 * 1000; // refresh stamp at most every 30 min per cart
  var stampedAt = 0;
  var stampedCart = '';
  var inFlight = false;
  var pending = null;

  // Interaction is a property of the browsing session, not of one page view:
  // a shopper who clicks "add to cart" then navigates to /cart must still
  // count as having interacted. Persist it so it survives navigation.
  var sawInteraction = false;
  try {
    sawInteraction = window.sessionStorage.getItem(IKEY) === '1';
  } catch (e) { /* storage blocked (private mode) — fall back to page scope */ }

  function onInteract() {
    if (!sawInteraction) {
      sawInteraction = true;
      try {
        window.sessionStorage.setItem(IKEY, '1');
      } catch (e) { /* non-fatal */ }
    }
    document.removeEventListener('pointerdown', onInteract, true);
    document.removeEventListener('keydown', onInteract, true);
    document.removeEventListener('touchstart', onInteract, true);
  }
  if (!sawInteraction) {
    document.addEventListener('pointerdown', onInteract, true);
    document.addEventListener('keydown', onInteract, true);
    document.addEventListener('touchstart', onInteract, true);
  }

  function cartSuffix(token) {
    var t = String(token || '').split('?')[0];
    return t.slice(-12);
  }

  function automationFlags() {
    var flags = '';
    try {
      if (navigator.webdriver === true) flags += 'w';
      var ua = navigator.userAgent || '';
      var headlessTell =
        ua.indexOf('HeadlessChrome') !== -1 ||
        (window.outerWidth === 0 && window.outerHeight === 0) ||
        (/Chrome\//.test(ua) && !/Mobile|Android/.test(ua) && typeof window.chrome === 'undefined');
      if (headlessTell) flags += 'x';
    } catch (e) { /* tells are best-effort */ }
    return flags;
  }

  // Release the lock and run any stamp that was deferred while we were busy,
  // so a checkout-click stamp is never silently swallowed by a page-load one.
  function done() {
    inFlight = false;
    if (pending) {
      var p = pending;
      pending = null;
      stamp(p.force, p.keepalive);
    }
  }

  function stamp(force, keepalive) {
    try {
      if (inFlight) {
        if (force) pending = { force: true, keepalive: keepalive };
        return;
      }
      var now = Date.now();
      if (!force && now - stampedAt < RESTAMP_MS) return;
      inFlight = true;
      fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', {
        credentials: 'same-origin',
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart || !cart.token || cart.item_count === 0) { done(); return; }
          var suffix = cartSuffix(cart.token);
          if (!force && suffix === stampedCart && Date.now() - stampedAt < RESTAMP_MS) { done(); return; }
          var token = 'v1.' + Math.floor(Date.now() / 1000) + '.' + suffix + '.' + (sawInteraction ? 'h' : 'n') + automationFlags();
          var attrs = {};
          attrs[ATTR] = token;
          var opts = {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: attrs }),
          };
          if (keepalive) opts.keepalive = true;
          return fetch(
            (window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js',
            opts
          ).then(function (res) {
            if (res && res.ok) {
              stampedAt = Date.now();
              stampedCart = suffix;
            }
            done();
          });
        })
        .catch(function () { done(); });
    } catch (e) {
      done();
    }
  }

  // 1. Page load: covers cart page / drawer views and carts built earlier or on another device.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { stamp(false); });
  } else {
    stamp(false);
  }

  // 2. Any successful AJAX cart change (add-to-cart, quantity change) via the theme's pub/sub.
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { stamp(true); });
    }
  } catch (e) { /* pub/sub unavailable — load + checkout hooks still cover us */ }

  // 3. Checkout button click: freshest possible stamp before handoff. Capture phase,
  //    fire-and-forget with keepalive so navigation doesn't cancel the request.
  document.addEventListener(
    'click',
    function (evt) {
      try {
        var el = evt.target && evt.target.closest ? evt.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]') : null;
        if (el) stamp(true, true);
      } catch (e) { /* never interfere with checkout */ }
    },
    true
  );
})();
